/**
 * Integração com o Autonomous (fases 1, 2 e 5): "Abrir no Autonomous" nos links para telas que ele assumiu, a conexão
 * "Autonomous" em destaque no catálogo, os tópicos e os retratos na tela da conexão e o assistente de regra sem a
 * parte de notificação.
 */
const en = {
  'autonomous.open': 'Open in Autonomous',
  'autonomous.feed.obj': 'Daily snapshot in object storage, file {f}.jsonl.gz (and the initial load)',
  'autonomous.openHint': 'Opens this item in Autonomous, in a new tab',
  'autonomous.featured': 'Recommended for this installation',
  'autonomous.invite.title': 'Connect Autonomous',
  'autonomous.invite.text':
    'This installation is integrated with Autonomous. Connect it to publish every event category in its own Kafka topic and to write a daily snapshot to object storage.',
  'autonomous.invite.button': 'Connect Autonomous',
  'autonomous.topics.title': 'Kafka topics',
  'autonomous.topics.lead': 'One topic per category, in CloudEvents 1.0, with the event type in the key. Test connection creates the missing topics when Kafka allows it.',
  'autonomous.topics.none': 'No category selected: edit the connection and choose the events.',
  'autonomous.snap.title': 'Snapshots in object storage',
  'autonomous.snap.lead':
    'A daily snapshot (and the initial load when connected) in JSON Lines gzip, with a manifest.json holding the date, the counts and the sha256 of each file. When it ends, snapshot.ready goes to the bus pointing to the manifest.',
  'autonomous.snap.notConfigured': 'No bucket configured: edit the connection and fill in the object storage to turn on the snapshots.',
  'autonomous.snap.useOff': 'The daily snapshot use is off: only Generate snapshot now runs.',
  'autonomous.snap.bucket': 'Bucket and prefix',
  'autonomous.snap.retention': 'Kept for',
  'autonomous.snap.retentionDays': '{n} days',
  'autonomous.snap.last': 'Last snapshot',
  'autonomous.snap.lastOk': 'Last complete snapshot',
  'autonomous.snap.never': 'None yet',
  'autonomous.snap.generate': 'Generate snapshot now',
  'autonomous.snap.started': 'Snapshot started: it runs in the background.',
  'autonomous.snap.refresh': 'Refresh',
  'autonomous.snap.col.when': 'Started',
  'autonomous.snap.col.kind': 'Kind',
  'autonomous.snap.col.state': 'State',
  'autonomous.snap.col.rows': 'Rows',
  'autonomous.snap.col.size': 'Size',
  'autonomous.snap.col.manifest': 'Manifest',
  'autonomous.snap.kind.initial': 'Automatic (on connect)',
  'autonomous.snap.kind.daily': 'Daily',
  'autonomous.snap.kind.manual': 'Manual',
  'autonomous.snap.state.running': 'Running',
  'autonomous.snap.state.ok': 'Done',
  'autonomous.snap.state.partial': 'Partial',
  'autonomous.snap.state.error': 'Error',
  'autonomous.snap.initialTag': 'Initial load',
  'autonomous.snap.initialHint': 'The first snapshot of this connection that ended well: its manifest and snapshot.ready carry "initial": true.',
  'autonomous.echo.title': 'Echo filter',
  'autonomous.echo.lead':
    'Hive writes byAccount = {pattern} on the events made by the integration account (for example, alert.closed of a close requested by Autonomous). Autonomous ignores the events with these values:',
  'autonomous.echo.accountName': '<account-name>',
  'autonomous.echo.noPermission': 'Listing the API accounts needs the permission to manage the API. Use {pattern} with the name of the Autonomous integration account.',
  'autonomous.echo.none': 'No active integration account with approval on behalf of a person (Autonomous template). Create it in API and integration accounts; the value will be {pattern}.',
  'autonomous.echo.copy': 'Copy',
  'autonomous.rules.title': 'Who notifies is Autonomous',
  'autonomous.rules.note':
    'This installation is integrated with Autonomous: it notifies the people, so channels, on-call and escalation are not used here. The alert reaches Autonomous through the event bus (alert.*); tickets in the ITSM, the automatic fix and the NOC and SOC walls continue.',
  'autonomous.rules.review': 'By Autonomous (alert.* events)',
  'autonomous.rules.notices': 'Notices by Autonomous',
  'autonomous.rules.actionsLead': 'What happens when it fires. Autonomous notifies the people; tickets in the ITSM tools of the Integrations Center and the automatic fix continue here.',
};

const ptBR: Record<keyof typeof en, string> = {
  'autonomous.open': 'Abrir no Autonomous',
  'autonomous.feed.obj': 'Retrato diário no armazenamento de objetos, arquivo {f}.jsonl.gz (e a carga inicial)',
  'autonomous.openHint': 'Abre este item no Autonomous, numa nova aba',
  'autonomous.featured': 'Recomendada para esta instalação',
  'autonomous.invite.title': 'Conecte o Autonomous',
  'autonomous.invite.text':
    'Esta instalação está integrada ao Autonomous. Conecte-o para publicar cada categoria de evento no seu próprio tópico do Kafka e gravar um retrato diário no armazenamento de objetos.',
  'autonomous.invite.button': 'Conectar o Autonomous',
  'autonomous.topics.title': 'Tópicos do Kafka',
  'autonomous.topics.lead': 'Um tópico por categoria, em CloudEvents 1.0, com o tipo do evento na chave. Testar conexão cria os tópicos que faltam quando o Kafka permite.',
  'autonomous.topics.none': 'Nenhuma categoria escolhida: edite a conexão e escolha os eventos.',
  'autonomous.snap.title': 'Retratos no armazenamento de objetos',
  'autonomous.snap.lead':
    'Um retrato diário (e a carga inicial ao conectar) em JSON Lines gzip, com um manifest.json com a data, as contagens e o sha256 de cada arquivo. Ao terminar, snapshot.ready sai no barramento apontando o manifest.',
  'autonomous.snap.notConfigured': 'Nenhum bucket configurado: edite a conexão e preencha o armazenamento de objetos para ligar os retratos.',
  'autonomous.snap.useOff': 'O uso de retrato diário está desligado: só Gerar retrato agora roda.',
  'autonomous.snap.bucket': 'Bucket e prefixo',
  'autonomous.snap.retention': 'Guardados por',
  'autonomous.snap.retentionDays': '{n} dias',
  'autonomous.snap.last': 'Último retrato',
  'autonomous.snap.lastOk': 'Último retrato completo',
  'autonomous.snap.never': 'Nenhum ainda',
  'autonomous.snap.generate': 'Gerar retrato agora',
  'autonomous.snap.started': 'Retrato iniciado: ele roda em segundo plano.',
  'autonomous.snap.refresh': 'Atualizar',
  'autonomous.snap.col.when': 'Início',
  'autonomous.snap.col.kind': 'Tipo',
  'autonomous.snap.col.state': 'Situação',
  'autonomous.snap.col.rows': 'Linhas',
  'autonomous.snap.col.size': 'Tamanho',
  'autonomous.snap.col.manifest': 'Manifest',
  'autonomous.snap.kind.initial': 'Automático (ao conectar)',
  'autonomous.snap.kind.daily': 'Diário',
  'autonomous.snap.kind.manual': 'Manual',
  'autonomous.snap.state.running': 'Rodando',
  'autonomous.snap.state.ok': 'Concluído',
  'autonomous.snap.state.partial': 'Parcial',
  'autonomous.snap.state.error': 'Erro',
  'autonomous.snap.initialTag': 'Carga inicial',
  'autonomous.snap.initialHint': 'O primeiro retrato desta conexão que terminou bem: o manifest e o snapshot.ready dele trazem "initial": true.',
  'autonomous.echo.title': 'Filtro de eco',
  'autonomous.echo.lead':
    'O Hive grava byAccount = {pattern} nos eventos feitos pela conta de integração (por exemplo, o alert.closed de um fechamento pedido pelo Autonomous). O Autonomous ignora os eventos com estes valores:',
  'autonomous.echo.accountName': '<nome-da-conta>',
  'autonomous.echo.noPermission': 'Listar as contas de API exige a permissão de administrar a API. Use {pattern} com o nome da conta de integração do Autonomous.',
  'autonomous.echo.none': 'Nenhuma conta de integração ativa com aprovação em nome da pessoa (modelo Autonomous). Crie-a em API e contas de integração; o valor será {pattern}.',
  'autonomous.echo.copy': 'Copiar',
  'autonomous.rules.title': 'Quem avisa é o Autonomous',
  'autonomous.rules.note':
    'Esta instalação está integrada ao Autonomous: é ele quem avisa as pessoas, por isso canais, plantão e escalonamento não são usados aqui. O alerta chega ao Autonomous pelo barramento de eventos (alert.*); o chamado no ITSM, a correção automática e os murais NOC e SOC continuam.',
  'autonomous.rules.review': 'Pelo Autonomous (eventos alert.*)',
  'autonomous.rules.notices': 'Avisos pelo Autonomous',
  'autonomous.rules.actionsLead': 'O que acontece quando dispara. Quem avisa as pessoas é o Autonomous; chamados nas ferramentas de ITSM da Central de integrações e a correção automática continuam aqui.',
};

const es: Record<keyof typeof en, string> = {
  'autonomous.open': 'Abrir en Autonomous',
  'autonomous.feed.obj': 'Instantánea diaria en el almacenamiento de objetos, archivo {f}.jsonl.gz (y la carga inicial)',
  'autonomous.openHint': 'Abre este elemento en Autonomous, en una nueva pestaña',
  'autonomous.featured': 'Recomendada para esta instalación',
  'autonomous.invite.title': 'Conecte Autonomous',
  'autonomous.invite.text':
    'Esta instalación está integrada con Autonomous. Conéctelo para publicar cada categoría de evento en su propio tópico de Kafka y escribir una instantánea diaria en el almacenamiento de objetos.',
  'autonomous.invite.button': 'Conectar Autonomous',
  'autonomous.topics.title': 'Tópicos de Kafka',
  'autonomous.topics.lead': 'Un tópico por categoría, en CloudEvents 1.0, con el tipo del evento en la clave. Probar conexión crea los tópicos que faltan cuando Kafka lo permite.',
  'autonomous.topics.none': 'Ninguna categoría elegida: edite la conexión y elija los eventos.',
  'autonomous.snap.title': 'Instantáneas en el almacenamiento de objetos',
  'autonomous.snap.lead':
    'Una instantánea diaria (y la carga inicial al conectar) en JSON Lines gzip, con un manifest.json con la fecha, los conteos y el sha256 de cada archivo. Al terminar, snapshot.ready sale en el bus apuntando al manifest.',
  'autonomous.snap.notConfigured': 'Ningún bucket configurado: edite la conexión y complete el almacenamiento de objetos para activar las instantáneas.',
  'autonomous.snap.useOff': 'El uso de instantánea diaria está desactivado: solo se ejecuta Generar instantánea ahora.',
  'autonomous.snap.bucket': 'Bucket y prefijo',
  'autonomous.snap.retention': 'Se guardan por',
  'autonomous.snap.retentionDays': '{n} días',
  'autonomous.snap.last': 'Última instantánea',
  'autonomous.snap.lastOk': 'Última instantánea completa',
  'autonomous.snap.never': 'Ninguna todavía',
  'autonomous.snap.generate': 'Generar instantánea ahora',
  'autonomous.snap.started': 'Instantánea iniciada: se ejecuta en segundo plano.',
  'autonomous.snap.refresh': 'Actualizar',
  'autonomous.snap.col.when': 'Inicio',
  'autonomous.snap.col.kind': 'Tipo',
  'autonomous.snap.col.state': 'Estado',
  'autonomous.snap.col.rows': 'Filas',
  'autonomous.snap.col.size': 'Tamaño',
  'autonomous.snap.col.manifest': 'Manifest',
  'autonomous.snap.kind.initial': 'Automática (al conectar)',
  'autonomous.snap.kind.daily': 'Diaria',
  'autonomous.snap.kind.manual': 'Manual',
  'autonomous.snap.state.running': 'En ejecución',
  'autonomous.snap.state.ok': 'Terminada',
  'autonomous.snap.state.partial': 'Parcial',
  'autonomous.snap.state.error': 'Error',
  'autonomous.snap.initialTag': 'Carga inicial',
  'autonomous.snap.initialHint': 'La primera instantánea de esta conexión que terminó bien: su manifest y su snapshot.ready traen "initial": true.',
  'autonomous.echo.title': 'Filtro de eco',
  'autonomous.echo.lead':
    'Hive graba byAccount = {pattern} en los eventos hechos por la cuenta de integración (por ejemplo, el alert.closed de un cierre pedido por Autonomous). Autonomous ignora los eventos con estos valores:',
  'autonomous.echo.accountName': '<nombre-de-la-cuenta>',
  'autonomous.echo.noPermission': 'Listar las cuentas de API exige el permiso de administrar la API. Use {pattern} con el nombre de la cuenta de integración de Autonomous.',
  'autonomous.echo.none': 'Ninguna cuenta de integración activa con aprobación en nombre de la persona (plantilla Autonomous). Créela en API y cuentas de integración; el valor será {pattern}.',
  'autonomous.echo.copy': 'Copiar',
  'autonomous.rules.title': 'Quien avisa es Autonomous',
  'autonomous.rules.note':
    'Esta instalación está integrada con Autonomous: es quien avisa a las personas, por eso canales, guardia y escalamiento no se usan aquí. La alerta llega a Autonomous por el bus de eventos (alert.*); el ticket en el ITSM, la corrección automática y los murales NOC y SOC continúan.',
  'autonomous.rules.review': 'Por Autonomous (eventos alert.*)',
  'autonomous.rules.notices': 'Avisos por Autonomous',
  'autonomous.rules.actionsLead': 'Qué pasa cuando se dispara. Quien avisa a las personas es Autonomous; los tickets en las herramientas de ITSM del Centro de integraciones y la corrección automática siguen aquí.',
};

export const autonomousLinksMessages = { en, 'pt-BR': ptBR, es } as const;
